import type { UseQueryResult } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import type {
	DokladyDokladDto,
	DokladyDokladSeznamDto,
	OsobySouhlasDto,
	OsobySouhlasSeznamDto,
} from '@gov-nx/api';
import {
	consentPreviewsQuery,
	identityDocumentQuery,
	sendSubmissionQuery,
	useAxiosInstance,
	usePoMutation,
	usePoQueries,
	usePoQuery,
} from '@gov-nx/api';
import { GovError } from '@gov-nx/core/app';
import { useMessageEvents } from '@gov-nx/core/events';
import { useProcessControl, useServiceAuthorization } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import { compose, is } from '@gov-nx/core/types';
import type { PageCode } from '../../definitions/codes';
import type { ConsentRequest } from '../detail/context.types';
import { prepareSubmitData } from '../detail/utils';
import type {
	ConsentManagementContextTypes,
	ConsentManagementControls,
} from './context.types';
import { useForm } from '../useForm';
import { AUTHORIZATION_SERVICE_NAME } from '../constants';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

const SpravaSouhlasuSeznamContext =
	createContext<Nullable<ConsentManagementContextTypes>>(null);

interface SpravaSouhlasuContextProviderProps {
	children: React.ReactNode;
	code: PageCode;
}

export function ConsentManagementSeznamContextProvider({
	children,
	code,
}: SpravaSouhlasuContextProviderProps) {
	const { axiosInstance } = useAxiosInstance();
	const { controls, setControls } =
		useProcessControl<ConsentManagementControls>({
			initialLoading: true,
			consentToRecall: null,
		});
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { t } = useLocale(code);
	const [consentData, setConsentData] = useState<OsobySouhlasDto[]>([]);
	const [idDocuments, setIdDocuments] = useState<DokladyDokladDto[]>([]);

	const authContext = useAutorizaceContext();

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(AUTHORIZATION_SERVICE_NAME);

	const consentQuerySetup = {
		queryKey: ['souhlasy'],
		queryFn: async () => consentPreviewsQuery(axiosInstance),
	};

	const consentQuery = usePoQuery({
		...consentQuerySetup,
		onSuccess: (data) => setConsentData(data.seznam || []),
	});

	const mainQueries = usePoQueries<
		[
			UseQueryResult<OsobySouhlasSeznamDto, AxiosError<AxiosResponse>>,
			UseQueryResult<DokladyDokladSeznamDto, AxiosError<AxiosResponse>>,
		]
	>({
		queries: [
			{
				...consentQuerySetup,
				onError: (error: Error) =>
					setControls({ initialError: new GovError(error.message) }),
			},
			{
				queryKey: ['doklady'],
				queryFn: async () => identityDocumentQuery(axiosInstance),
				onError: (error: Error) =>
					setControls({ initialError: new GovError(error.message) }),
				refetchOnWindowFocus: false,
				retry: 0,
			},
		],
	});

	const queriesResponses = mainQueries
		.map((result) => {
			if (result.isError || result.isFetched) {
				return result.data ?? false;
			}
			return undefined;
		})
		.filter(is);

	const isQueriesFetched = queriesResponses.length === mainQueries.length;

	useEffect(() => {
		if (isQueriesFetched && controls.initialLoading) {
			const consents = queriesResponses[0]
				? (queriesResponses[0] as OsobySouhlasSeznamDto)
				: null;
			const identityDocuments = queriesResponses[1]
				? (queriesResponses[1] as AxiosResponse<DokladyDokladSeznamDto>)
				: null;

			setConsentData(consents?.seznam || []);
			setIdDocuments(identityDocuments?.data.seznam || []);
			setControls({ initialLoading: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [isQueriesFetched]);

	const reloadConsents = useCallback(async () => {
		await consentQuery.refetch();
		setControls({ processLoading: false });
		toastMessageSuccess(t('formular.zprava.souhlas-odvolan'));
	}, [consentQuery, setControls, toastMessageSuccess, t]);

	const onDialogClose = () => {
		setControls({ consentToRecall: null });
		formDefinition.formMethods.reset();
	};

	const submitMutation = usePoMutation<
		AxiosResponse<number, void>,
		ConsentRequest
	>({
		mutationFn: compose(sendSubmissionQuery(axiosInstance), prepareSubmitData),
		onError: (error) => {
			toastMessageError(t('formular.zprava.chyba'));
			setControls({
				processError: error,
				processLoading: false,
			});
		},
		onSuccess: async (data) => {
			onDialogClose();

			const nonHighAuthorizationFlow = async () => {
				reloadConsents();
			};

			if (authContext) {
				authContext?.onInitAuthorization(
					AUTHORIZATION_SERVICE_NAME,
					data.headers['x-autorizaceid'],
					nonHighAuthorizationFlow
				);
			} else {
				nonHighAuthorizationFlow();
			}
		},
	});

	const formDefinition = useForm(controls.processLoading, isLowAuthorization);

	const handleSubmit = formDefinition.formMethods.handleSubmit(() => {
		const consent = controls.consentToRecall;
		setControls({
			processError: null,
			processLoading: true,
		});
		submitMutation.mutate({
			kodPodani: 'ZR10_ODVOLANI',
			souhlasId: consent?.id,
			nazevOsoby: consent?.nazevOsoby,
			datovaSchrankaId: consent?.datovaSchrankaId,
			typCisloID: idDocuments[0].cisloDokladu,
		});
	});

	return (
		<SpravaSouhlasuSeznamContext.Provider
			value={{
				code,
				controls,
				consentData,
				formDefinition,
				isLowAuthorization,
				isAuthorizationEnforced,
				setControls,
				handleSubmit,
				onDialogClose,
			}}>
			{children}
		</SpravaSouhlasuSeznamContext.Provider>
	);
}

export const useSpravaSouhlasuSeznamContext =
	(): ConsentManagementContextTypes =>
		useContext(SpravaSouhlasuSeznamContext) as ConsentManagementContextTypes;
